import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 앱 전역: 포커스된 '한 줄 텍스트 input'에 입력값이 있으면 오른쪽에 x(지우기) 버튼을 오버레이.
 * - textarea / date·time류 / checkbox·radio / number / file 등은 제외.
 * - controlled input 도 비워지도록 native value setter + input/change 이벤트를 디스패치.
 * - data-no-clear 속성이 있으면 제외.
 */
const CLEARABLE_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', '']);

// iOS 는 키보드가 뜨면 position:fixed 가 비주얼 뷰포트 기준으로 동작해 보정이 필요하지만,
// Android(삼성 인터넷/크롬)는 fixed 가 레이아웃 뷰포트 기준이라 보정하면 오히려 어긋난다.
const IS_IOS =
  typeof navigator !== 'undefined' &&
  (/ip(hone|ad|od)/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1));

function isClearable(el) {
  if (!el || el.tagName !== 'INPUT') return false;
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  if (!CLEARABLE_TYPES.has(type)) return false;
  if (el.disabled || el.readOnly) return false;
  if (el.dataset && el.dataset.noClear !== undefined) return false;
  return true;
}

export default function GlobalInputClear() {
  const [target, setTarget] = useState(null);
  const [hasValue, setHasValue] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const rafRef = useRef(0);

  const reposition = useCallback(() => {
    const el = target;
    if (!el || !document.contains(el)) {
      setPos((prev) => (prev === null ? prev : null));
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      setPos((prev) => (prev === null ? prev : null));
      return;
    }
    // iOS 에서만 visualViewport offset 보정(키보드 시 fixed 가 비주얼 뷰포트 기준).
    // Android 는 보정 없이 getBoundingClientRect 좌표가 그대로 맞음.
    const vv = window.visualViewport;
    const offsetX = IS_IOS && vv ? vv.offsetLeft : 0;
    const offsetY = IS_IOS && vv ? vv.offsetTop : 0;
    // 버튼 중심을 input 오른쪽 끝에서 안쪽(18px) · 세로 중앙에 — 끝에 바짝 붙지 않게 여백
    const top = r.top + r.height / 2 + offsetY;
    const left = r.right - 18 + offsetX;
    // 위치가 실제로 바뀔 때만 갱신(매 프레임 재렌더 방지)
    setPos((prev) =>
      prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5
        ? prev
        : { top, left }
    );
  }, [target]);

  // 포커스/입력 추적 (한 번만 등록)
  useEffect(() => {
    const onFocusIn = (e) => {
      if (isClearable(e.target)) {
        setTarget(e.target);
        setHasValue((e.target.value || '').length > 0);
      }
    };
    const onFocusOut = () => {
      // blur 후 실제 포커스 대상이 정해진 다음 판단
      setTimeout(() => {
        const a = document.activeElement;
        if (isClearable(a)) {
          setTarget(a);
          setHasValue((a.value || '').length > 0);
        } else {
          setTarget(null);
        }
      }, 0);
    };
    const onInput = (e) => {
      if (e.target === document.activeElement && isClearable(e.target)) {
        setHasValue((e.target.value || '').length > 0);
      }
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('input', onInput, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('input', onInput, true);
    };
  }, []);

  // 포커스된 input 의 오른쪽에 x 버튼 자리만큼 패딩을 확보 — 텍스트가 버튼 뒤로 들어가지 않게.
  // (포커스 해제 시 원래 패딩으로 복원)
  useEffect(() => {
    const el = target;
    if (!el) return undefined;
    const prevInline = el.style.paddingRight;
    const curPad = parseFloat(getComputedStyle(el).paddingRight) || 0;
    const RESERVE = 30;
    if (curPad < RESERVE) el.style.paddingRight = `${RESERVE}px`;
    return () => {
      el.style.paddingRight = prevInline;
    };
  }, [target]);

  // 대상이 있는 동안 매 프레임 위치 추종 — 스크롤/리사이즈뿐 아니라
  // 에러 메시지 등장·제거 같은 레이아웃 변화에도 항상 input 에 정확히 붙어 있게.
  useEffect(() => {
    if (!target) {
      setPos((prev) => (prev === null ? prev : null));
      return undefined;
    }
    const loop = () => {
      reposition();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, reposition]);

  const clear = () => {
    const el = target;
    if (!el) return;
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;
    if (setter) setter.call(el, '');
    else el.value = '';
    // React(controlled) 및 일반 핸들러 모두 반응하도록
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    el.focus();
    setHasValue(false);
  };

  if (!target || !pos || !hasValue) return null;

  return (
    <button
      ref={btnRef}
      type="button"
      className="global-input-clear-btn"
      aria-label="입력 지우기"
      title="지우기"
      style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
      // input blur 방지 — 버튼 클릭해도 포커스 유지
      onMouseDown={(e) => e.preventDefault()}
      onClick={clear}
    >
      <svg
        viewBox="0 0 24 24"
        width="10"
        height="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <line x1="5" y1="5" x2="19" y2="19" />
        <line x1="19" y1="5" x2="5" y2="19" />
      </svg>
    </button>
  );
}
